import { useState, useEffect } from 'react'
import { T } from '../../styles/theme'
import { WeightChart, BalanceChart, MacroChart, CompositionChart } from '../../components/charts'
import { SectionLabel, Card } from '../../components/ui'
import { getBalanceTargets, withHistoricalBalance, fillMissingDays } from '../../services/balanceCalc'
import { bucketModeFor, aggregateHistory } from '../../services/historyAggregation'

const MAX_DIAS_ATRAS_PACIENTE = 3

function todayStr() { return new Date().toISOString().split('T')[0] }
function minDateStr(diasAtras) {
  const d = new Date()
  d.setDate(d.getDate() - diasAtras)
  return d.toISOString().split('T')[0]
}
function clampFecha(fecha, diasAtras) {
  const max = todayStr()
  const min = minDateStr(diasAtras)
  if (!fecha || fecha > max) return max
  if (fecha < min) return min
  return fecha
}

export default function Progreso({ data }) {
  const { metrics, goals, addMetric, balanceHistory = [], activityProfile, targetsHistory = [], patientId, fetchBalanceHistory } = data
  const tdee = activityProfile?.tdee ?? null
  const [progressTab, setProgressTab] = useState('peso')
  const [rangeWeeks, setRangeWeeks] = useState(6)
  const [rangeHistory, setRangeHistory] = useState(balanceHistory)
  const [modal, setModal] = useState(false)
  const [form, setForm] = useState({
    peso_kg: '', grasa_pct: '', masa_magra_pct: '',
    masa_muscular_kg: '', perimetro_abdominal_cm: '', metodo: 'bascula', fecha: todayStr(),
  })
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!patientId) return
    let cancelled = false
    fetchBalanceHistory(patientId, rangeWeeks * 7).then(result => {
      if (!cancelled) setRangeHistory(result)
    })
    return () => { cancelled = true }
  }, [patientId, rangeWeeks]) // eslint-disable-line react-hooks/exhaustive-deps

  const now = new Date()
  const cutoff = new Date(now)
  cutoff.setDate(cutoff.getDate() - rangeWeeks * 7)
  const filtered = metrics
    .filter(m => m.peso_kg && new Date(m.fecha) >= cutoff)
    .sort((a, b) => new Date(a.fecha) - new Date(b.fecha))
  const weightData = filtered.map(m => ({
    label: new Date(m.fecha).toLocaleDateString('es', { day: '2-digit', month: 'short' }),
    fecha: m.fecha,
    v: m.peso_kg
  }))

  // El día de hoy se excluye del histórico: aún está en curso (faltan
  // comidas por registrar) y desvirtúa la gráfica y las medias. Para
  // ver el dato de hoy en tiempo real ya está la rueda de "Hoy".
  const historyDoneRaw = rangeHistory.filter(d => d.fecha !== todayStr())
  const kcalObj = goals?.kcal_objetivo ?? 2000
  const { minTarget, maxTarget, isApprox } = getBalanceTargets(kcalObj, tdee)
  // Cada día usa el objetivo/TDEE que tenías vigente EN ESE MOMENTO,
  // no el actual — así un cambio de objetivo no recolorea el pasado.
  // Los días sin ninguna comida registrada se marcan como "sin datos":
  // no se dibujan como barra, ni cuentan en las medias.
  const historyDone = withHistoricalBalance(fillMissingDays(historyDoneRaw, todayStr()), targetsHistory, kcalObj, tdee)
  const historyWithData = historyDone.filter(d => !d.sinDatos)
  const bucketMode = bucketModeFor(rangeWeeks)
  const aggregatedHistory = aggregateHistory(historyDone, bucketMode)
  const hayDiasSinDatos = aggregatedHistory.some(d => d.sinDatos)
  const avgDailyBalance = historyWithData.length > 0
    ? Math.round(historyWithData.reduce((s, d) => s + d.v, 0) / historyWithData.length)
    : null

  const latest = metrics[0]
  const oldest = metrics.length > 1 ? metrics[metrics.length - 1] : null
  function delta(key) {
    if (!latest || !oldest || latest[key] == null) return null
    return Math.round((latest[key] - oldest[key]) * 10) / 10
  }

  async function handleSave() {
    setSaving(true)
    await addMetric({
      peso_kg: parseFloat(form.peso_kg) || null,
      grasa_pct: parseFloat(form.grasa_pct) || null,
      masa_magra_pct: parseFloat(form.masa_magra_pct) || null,
      masa_muscular_kg: parseFloat(form.masa_muscular_kg) || null,
      perimetro_abdominal_cm: parseFloat(form.perimetro_abdominal_cm) || null,
      metodo: form.metodo,
    }, clampFecha(form.fecha, MAX_DIAS_ATRAS_PACIENTE))
    setSaving(false)
    setModal(false)
    setForm({ peso_kg: '', grasa_pct: '', masa_magra_pct: '', masa_muscular_kg: '', perimetro_abdominal_cm: '', metodo: 'bascula', fecha: todayStr() })
  }

  const inp = (label, key, placeholder) => (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 13, color: T.muted, marginBottom: 6 }}>{label}</div>
      <input type="number" placeholder={placeholder} value={form[key]}
        onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
        style={{ width: '100%', background: T.bg, border: `1px solid ${T.cardB}`, borderRadius: 12, padding: '12px 16px', color: T.text, fontSize: 15, fontFamily: 'DM Sans', outline: 'none' }}
      />
    </div>
  )

  return (
    <div>
      <div style={{ padding: '52px 20px 12px' }}>
        <div style={{ fontSize: 26, fontWeight: 700, letterSpacing: -0.5 }}>Progreso</div>
        <div style={{ fontSize: 13, color: T.muted, marginTop: 2 }}>Tu evolución</div>
      </div>

      {/* Stats actuales */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, margin: '0 16px 12px' }}>
        {[
          [latest?.peso_kg ? latest.peso_kg + ' kg' : '—', 'Peso actual', delta('peso_kg'), 'kg'],
          [latest?.grasa_pct ? latest.grasa_pct + '%' : '—', 'Grasa corporal', delta('grasa_pct'), '%'],
          [latest?.masa_magra_pct ? latest.masa_magra_pct + '%' : '—', 'Masa magra', delta('masa_magra_pct'), '%'],
          [latest?.perimetro_abdominal_cm ? latest.perimetro_abdominal_cm + ' cm' : '—', 'Perím. abdominal', delta('perimetro_abdominal_cm'), 'cm'],
        ].map(([v, l, d, u]) => (
          <div key={l} style={{ background: T.card, border: `1px solid ${T.cardB}`, borderRadius: 14, padding: 14 }}>
            <div style={{ fontSize: 20, fontWeight: 700, fontFamily: 'DM Mono', color: T.text }}>{v}</div>
            <div style={{ fontSize: 11, color: T.muted, marginTop: 3 }}>{l}</div>
            {d !== null && (
              <div style={{ fontSize: 12, fontWeight: 600, marginTop: 4, color: d <= 0 ? T.accent : T.danger }}>
                {d > 0 ? '+' : ''}{d} {u}
              </div>
            )}
          </div>
        ))}
      </div>

      <button onClick={() => setModal(true)} style={{
        display: 'block', margin: '0 16px 16px', padding: '11px 0',
        width: 'calc(100% - 32px)', borderRadius: 12,
        background: T.accentSoft, border: `1px solid ${T.accent}`,
        color: T.accent, fontSize: 13, fontWeight: 600, cursor: 'pointer'
      }}>
        ➕ Añadir medición
      </button>

      {/* Selector pestañas */}
      <div style={{ display: 'flex', gap: 6, margin: '0 16px 12px' }}>
        {[['peso', 'Peso'], ['balance', 'Balance'], ['macros', 'Macros'], ['comp', 'Composición']].map(([k, l]) => (
          <button key={k} onClick={() => setProgressTab(k)} style={{
            flex: 1, padding: '8px 0', borderRadius: 10,
            border: `1px solid ${progressTab === k ? T.accent : T.cardB}`,
            background: progressTab === k ? T.accentSoft : T.card,
            color: progressTab === k ? T.accent : T.muted,
            fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'DM Sans'
          }}>{l}</button>
        ))}
      </div>

      {/* Selector de rango (afecta a las 4 pestañas) */}
      <div style={{ display: 'flex', gap: 6, margin: '0 16px 12px' }}>
        {[[2, '2 sem'], [4, '1 mes'], [12, '3 mes'], [24, '6 mes'], [52, 'Todo']].map(([w, l]) => (
          <button key={w} onClick={() => setRangeWeeks(w)} style={{
            flex: 1, padding: '6px 0', borderRadius: 8,
            border: `1px solid ${rangeWeeks === w ? T.accent : T.cardB}`,
            background: rangeWeeks === w ? T.accentSoft : T.card,
            color: rangeWeeks === w ? T.accent : T.muted,
            fontSize: 11, cursor: 'pointer', fontFamily: 'DM Sans'
          }}>{l}</button>
        ))}
      </div>

      {/* PESTAÑA PESO */}
      {progressTab === 'peso' && <>
        <SectionLabel>
          Evolución del peso
        </SectionLabel>
        <div style={{ background: T.card, border: `1px solid ${T.cardB}`, borderRadius: 16, padding: 16, margin: '0 16px 12px' }}>
          <WeightChart data={weightData} goal={goals?.peso_objetivo} />
          {goals?.peso_objetivo && (
            <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <div style={{ width: 12, height: 2, background: T.accent, borderRadius: 1 }} />
                <span style={{ fontSize: 11, color: T.muted }}>Peso real</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <div style={{ width: 12, height: 2, background: T.warn, borderRadius: 1 }} />
                <span style={{ fontSize: 11, color: T.muted }}>Objetivo ({goals.peso_objetivo} kg)</span>
              </div>
            </div>
          )}
        </div>
        {metrics.length > 0 && <>
          <SectionLabel>
            Historial de mediciones
          </SectionLabel>
          {metrics.slice(0, 5).map((m, i) => (
            <div key={i} style={{ background: T.card, border: `1px solid ${T.cardB}`, borderRadius: 12, padding: '12px 16px', margin: '0 16px 8px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                <span style={{ fontSize: 13, fontWeight: 600 }}>
                  {new Date(m.fecha).toLocaleDateString('es', { day: 'numeric', month: 'long', year: 'numeric' })}
                </span>
                <span style={{ fontSize: 11, color: T.muted, textTransform: 'capitalize' }}>{m.metodo}</span>
              </div>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                {m.peso_kg && <span style={{ fontSize: 12, color: T.sub }}>⚖️ {m.peso_kg} kg</span>}
                {m.grasa_pct && <span style={{ fontSize: 12, color: T.sub }}>🔥 {m.grasa_pct}% grasa</span>}
                {m.perimetro_abdominal_cm && <span style={{ fontSize: 12, color: T.sub }}>📏 {m.perimetro_abdominal_cm} cm</span>}
              </div>
            </div>
          ))}
        </>}
      </>}

      {/* PESTAÑA BALANCE */}
      {progressTab === 'balance' && <>
        <SectionLabel>
          Balance calórico
        </SectionLabel>
        <Card>
          <BalanceChart
            data={aggregatedHistory}
            minTarget={minTarget}
            maxTarget={maxTarget}
          />
          <div style={{ marginTop: 8, padding: '8px 10px', background: T.accentSoft, borderRadius: 8, fontSize: 12, color: T.accent }}>
            Zona verde = tu objetivo de déficit/superávit real{isApprox ? ' (aproximado)' : ''}
          </div>
          {hayDiasSinDatos && (
            <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: T.muted }}>
              <div style={{ width: 10, height: 14, borderRadius: 2, background: T.muted, opacity: 0.3 }} />
              Sin registro (no cuenta en las medias)
            </div>
          )}
          {isApprox && (
            <div style={{ marginTop: 6, fontSize: 11, color: T.warn }}>
              Falta calcular tu gasto energético real — tu profesional aún no lo ha hecho, así que esta franja es aproximada.
            </div>
          )}
          {avgDailyBalance != null && (
            <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${T.cardB}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 12, color: T.muted }}>Media diaria del periodo{isApprox ? ' (aprox.)' : ''}</span>
              <span style={{ fontSize: 14, fontFamily: 'DM Mono', fontWeight: 700, color: avgDailyBalance <= 0 ? T.accent : T.danger }}>
                {avgDailyBalance > 0 ? '+' : ''}{avgDailyBalance} kcal
              </span>
            </div>
          )}
        </Card>
        <SectionLabel>
          Calorías ingeridas por {bucketMode === 'day' ? 'día' : bucketMode === 'week' ? 'semana' : 'mes'}
        </SectionLabel>
        <div style={{ background: T.card, border: `1px solid ${T.cardB}`, borderRadius: 16, padding: 16, margin: '0 16px 12px' }}>
          {aggregatedHistory.length > 0 ? aggregatedHistory.slice(-7).reverse().map((d, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: `1px solid ${T.cardB}` }}>
              <span style={{ fontSize: 13, color: T.sub }}>{d.label}</span>
              {d.sinDatos ? (
                <span style={{ fontSize: 12, color: T.muted }}>Sin registro</span>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ width: 60, height: 4, background: T.cardB, borderRadius: 2, overflow: 'hidden' }}>
                    <div style={{ height: '100%', borderRadius: 2, background: T.accent, width: `${Math.min(d.kcal / kcalObj * 100, 100)}%` }} />
                  </div>
                  <span style={{ fontSize: 13, fontFamily: 'DM Mono', fontWeight: 600, color: T.accent }}>{d.kcal} kcal</span>
                </div>
              )}
            </div>
          )) : (
            <div style={{ textAlign: 'center', color: T.muted, padding: 20, fontSize: 13 }}>Sin registros aún</div>
          )}
        </div>
      </>}

      {/* PESTAÑA MACROS */}
      {progressTab === 'macros' && <>
        <SectionLabel>
          Evolución de macros
        </SectionLabel>
        <div style={{ background: T.card, border: `1px solid ${T.cardB}`, borderRadius: 16, padding: 16, margin: '0 16px 12px' }}>
        <MacroChart data={aggregatedHistory.filter(d => !d.sinDatos)} goals={goals} />
          <div style={{ display: 'flex', gap: 16, marginTop: 10, flexWrap: 'wrap' }}>
            {[['Proteína', '#60A5FA'], ['Grasas', '#FBBF24'], ['Carbos', '#A78BFA']].map(([l, c]) => (
              <div key={l} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <div style={{ width: 12, height: 3, borderRadius: 2, background: c }} />
                <span style={{ fontSize: 11, color: T.muted }}>{l} <span style={{ opacity: 0.5 }}>— obj.</span></span>
              </div>
            ))}
          </div>
        </div>
        <SectionLabel>
          Promedio del periodo
        </SectionLabel>
        <Card>
          {historyWithData.length > 0 ? (() => {
            const avg = (key) => Math.round(historyWithData.reduce((s, d) => s + d[key], 0) / historyWithData.length)
            return [
              ['🥩 Proteína', avg('prot') + ' g', goals?.proteina_objetivo ? `Obj: ${goals.proteina_objetivo}g` : ''],
              ['🥑 Grasas', avg('gras') + ' g', goals?.grasa_objetivo ? `Obj: ${goals.grasa_objetivo}g` : ''],
              ['🍞 Carbos', avg('carb') + ' g', goals?.carbohidratos_objetivo ? `Obj: ${goals.carbohidratos_objetivo}g` : ''],
              ['🔥 Calorías', avg('kcal') + ' kcal', goals?.kcal_objetivo ? `Obj: ${goals.kcal_objetivo}kcal` : ''],
            ].map(([l, v, obj]) => (
              <div key={l} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: `1px solid ${T.cardB}` }}>
                <span style={{ fontSize: 14, color: T.sub }}>{l}</span>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 14, fontWeight: 700, fontFamily: 'DM Mono' }}>{v}</div>
                  {obj && <div style={{ fontSize: 11, color: T.muted }}>{obj}</div>}
                </div>
              </div>
            ))
          })() : (
            <div style={{ textAlign: 'center', color: T.muted, padding: 20, fontSize: 13 }}>Sin registros aún</div>
          )}
        </Card>
      </>}

      {/* PESTAÑA COMPOSICIÓN */}
      {progressTab === 'comp' && <>
  <SectionLabel>
    Evolución composición corporal
  </SectionLabel>
  <div style={{ background: T.card, border: `1px solid ${T.cardB}`, borderRadius: 16, padding: 16, margin: '0 16px 12px' }}>
    {(() => {
      const compData = metrics
        .filter(m => (m.grasa_pct || m.masa_magra_pct) && new Date(m.fecha) >= cutoff)
        .sort((a, b) => new Date(a.fecha) - new Date(b.fecha))
        .map(m => ({
          label: new Date(m.fecha).toLocaleDateString('es', { day: '2-digit', month: 'short' }),
          grasa: m.grasa_pct,
          magra: m.masa_magra_pct,
        }))

      return (
        <>
          <CompositionChart data={compData} />
          <div style={{ display: 'flex', gap: 16, marginTop: 8, flexWrap: 'wrap' }}>
            {[['% Grasa', T.danger], ['% Masa magra', T.accent]].map(([l, c]) => (
              <div key={l} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <div style={{ width: 12, height: 3, borderRadius: 2, background: c }} />
                <span style={{ fontSize: 11, color: T.muted }}>{l}</span>
              </div>
            ))}
          </div>
        </>
      )
    })()}
  </div>

  {/* Resumen actual */}
  <SectionLabel>
    Medición más reciente
  </SectionLabel>
  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, margin: '0 16px 12px' }}>
    {[
      [latest?.peso_kg ? latest.peso_kg + ' kg' : '—', 'Peso', delta('peso_kg'), 'kg'],
      [latest?.grasa_pct ? latest.grasa_pct + '%' : '—', 'Grasa', delta('grasa_pct'), '%'],
      [latest?.masa_magra_pct ? latest.masa_magra_pct + '%' : '—', 'Masa magra', delta('masa_magra_pct'), '%'],
      [latest?.perimetro_abdominal_cm ? latest.perimetro_abdominal_cm + ' cm' : '—', 'Perímetro abd.', delta('perimetro_abdominal_cm'), 'cm'],
    ].map(([v, l, d, u]) => (
      <div key={l} style={{ background: T.card, border: `1px solid ${T.cardB}`, borderRadius: 14, padding: 14 }}>
        <div style={{ fontSize: 18, fontWeight: 700, fontFamily: 'DM Mono' }}>{v}</div>
        <div style={{ fontSize: 11, color: T.muted, marginTop: 3 }}>{l}</div>
        {d !== null && (
          <div style={{ fontSize: 12, fontWeight: 600, marginTop: 4, color: d <= 0 ? T.accent : T.danger }}>
            {d > 0 ? '+' : ''}{d} {u}
          </div>
        )}
      </div>
    ))}
  </div>
</>}

      {/* MODAL AÑADIR MEDICIÓN */}
      {modal && (
        <div onClick={e => { if (e.target === e.currentTarget) setModal(false) }}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 50, display: 'flex', alignItems: 'flex-end' }}>
          <div style={{ background: T.card, borderRadius: '24px 24px 0 0', padding: '20px 20px 40px', width: '100%', maxHeight: '85vh', overflowY: 'auto' }}>
            <div style={{ width: 36, height: 4, background: T.cardB, borderRadius: 2, margin: '0 auto 20px' }} />
            <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 16 }}>Nueva medición</div>
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 13, color: T.muted, marginBottom: 6 }}>Fecha</div>
              <input type="date" value={form.fecha} min={minDateStr(MAX_DIAS_ATRAS_PACIENTE)} max={todayStr()}
                onChange={e => setForm(f => ({ ...f, fecha: e.target.value }))}
                style={{ width: '100%', background: T.bg, border: `1px solid ${T.cardB}`, borderRadius: 12, padding: '12px 16px', color: T.text, fontSize: 15, fontFamily: 'DM Sans', outline: 'none' }}
              />
              <div style={{ fontSize: 11, color: T.muted, marginTop: 6 }}>Puedes registrar mediciones de hasta {MAX_DIAS_ATRAS_PACIENTE} días atrás</div>
            </div>
            {inp('Peso (kg)', 'peso_kg', 'ej: 72.4')}
            {inp('% Grasa corporal', 'grasa_pct', 'ej: 22')}
            {inp('% Masa magra', 'masa_magra_pct', 'ej: 78')}
            {inp('Masa muscular (kg)', 'masa_muscular_kg', 'ej: 28.5')}
            {inp('Perímetro abdominal (cm)', 'perimetro_abdominal_cm', 'ej: 86')}
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 13, color: T.muted, marginBottom: 8 }}>Método</div>
              <div style={{ display: 'flex', gap: 8 }}>
                {[['bascula', '⚖️ Báscula'], ['pliegues', '📏 Pliegues']].map(([k, l]) => (
                  <button key={k} onClick={() => setForm(f => ({ ...f, metodo: k }))} style={{
                    flex: 1, padding: '10px 0', borderRadius: 10,
                    border: `1px solid ${form.metodo === k ? T.accent : T.cardB}`,
                    background: form.metodo === k ? T.accentSoft : T.bg,
                    color: form.metodo === k ? T.accent : T.muted,
                    fontSize: 13, cursor: 'pointer', fontWeight: 600
                  }}>{l}</button>
                ))}
              </div>
            </div>
            <button onClick={handleSave} disabled={saving} style={{
              width: '100%', padding: 14, borderRadius: 14,
              background: T.accent, color: '#000', fontSize: 15,
              fontWeight: 700, border: 'none', cursor: 'pointer'
            }}>
              {saving ? 'Guardando...' : 'Guardar medición'}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}